import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Send, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const MAX_EMAILS = 10;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Pace sends so we stay well under Resend's ~10 req/s ceiling (≈5/s here).
const MIN_GAP_MS = 200;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));


interface Props {
  networkId: string | null | undefined;
  onSent?: () => void;
}

// Extract the JSON body from either a normal supabase-js response or a
// FunctionsHttpError (which hides the body inside error.context).
async function readInvokeOutcome(data: any, error: any): Promise<{ body: any; httpStatus: number }> {
  if (error) {
    if (error instanceof FunctionsHttpError) {
      const status = error.context?.status ?? 500;
      let body: any = null;
      try { body = await error.context.json(); } catch {
        try { body = { error: await error.context.text() }; } catch { body = { error: error.message }; }
      }
      return { body: body ?? { error: error.message }, httpStatus: status };
    }
    return { body: { error: error.message || "Network error" }, httpStatus: 0 };
  }
  return { body: data ?? {}, httpStatus: 200 };
}

export function BulkInviteBrokersCard({ networkId, onSent }: Props) {
  const [raw, setRaw] = useState("");
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [showMaxWarning, setShowMaxWarning] = useState(false);

  const parseEmails = (): string[] => {
    const parts = raw
      .split(/[,\n;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    return Array.from(new Set(parts));
  };

  const sendBatch = async (
    emails: string[],
    { resend = false }: { resend?: boolean } = {}
  ) => {
    const sent: string[] = [];
    const resent: string[] = [];
    const alreadyInvited: string[] = [];
    const alreadyMember: string[] = [];
    const failed: { email: string; reason: string }[] = [];
    let rateLimitedMsg: string | null = null;

    setProgress({ done: 0, total: emails.length });
    let lastStart = 0;

    for (let i = 0; i < emails.length; i++) {
      const email = emails[i];
      // Client-side pacing: never start sends faster than 1 per MIN_GAP_MS.
      const wait = Math.max(0, lastStart + MIN_GAP_MS - Date.now());
      if (wait > 0) await sleep(wait);

      let body: any = null;
      let httpStatus = 0;
      // Up to 3 attempts per email, backing off on rate-limit responses only.
      for (let attempt = 0; attempt < 3; attempt++) {
        lastStart = Date.now();
        const { data, error } = await supabase.functions.invoke("invite-broker-to-network", {
          body: { email, networkId: networkId || undefined, resend },
        });
        const outcome = await readInvokeOutcome(data, error);
        body = outcome.body;
        httpStatus = outcome.httpStatus;
        const limited = httpStatus === 429 || body?.rate_limited;
        if (!limited || attempt === 2) break;
        await sleep(1500 * Math.pow(2, attempt) + Math.floor(Math.random() * 300));
      }

      setProgress({ done: i + 1, total: emails.length });

      if (httpStatus === 429 || body?.rate_limited) {
        rateLimitedMsg = body?.error || "Invite rate limit reached — remaining invites can be re-sent.";
        failed.push({ email, reason: "rate limited" });
        continue;
      }
      if (body?.sent) {
        if (body?.resent) resent.push(email);
        else sent.push(email);
        continue;
      }
      if (body?.skipped) {
        if (body.reason === "already_invited") alreadyInvited.push(email);
        else if (body.reason === "already_member") alreadyMember.push(email);
        else failed.push({ email, reason: body.reason || "skipped" });
        continue;
      }
      failed.push({ email, reason: body?.error || `Request failed (${httpStatus || "?"})` });
    }

    setProgress(null);
    return { sent, resent, alreadyInvited, alreadyMember, failed, rateLimitedMsg };
  };


  const handleResend = async (emails: string[]) => {
    setSending(true);
    const r = await sendBatch(emails, { resend: true });
    setSending(false);
    if (r.resent.length || r.sent.length) {
      toast.success(`Re-sent invite to ${r.resent.length + r.sent.length} broker${r.resent.length + r.sent.length === 1 ? "" : "s"}`);
      onSent?.();
    } else if (r.rateLimitedMsg) {
      toast.error(r.rateLimitedMsg);
    } else if (r.failed.length) {
      toast.error(`Failed to resend: ${r.failed.map((f) => f.email).join(", ")}`);
    }
  };

  const handleSend = async () => {
    const emails = parseEmails();
    if (emails.length === 0) {
      toast.error("Enter at least one broker email");
      return;
    }
    if (emails.length > MAX_EMAILS) {
      setShowMaxWarning(true);
      return;
    }
    const invalid = emails.filter((e) => !EMAIL_RE.test(e));
    if (invalid.length) {
      toast.error(`Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`);
      return;
    }

    setSending(true);
    const { sent, alreadyInvited, alreadyMember, failed, rateLimitedMsg } = await sendBatch(emails);

    const parts: string[] = [];
    parts.push(`${sent.length} sent`);
    if (alreadyInvited.length) parts.push(`${alreadyInvited.length} already invited`);
    if (alreadyMember.length) parts.push(`${alreadyMember.length} already in network`);
    if (failed.length) parts.push(`${failed.length} failed`);
    const summary = parts.join(" · ");

    if (sent.length > 0) toast.success(summary);
    else if (failed.length === 0 && !rateLimitedMsg) toast.info(summary);
    else toast.error(summary);

    if (alreadyInvited.length) {
      toast(
        `${alreadyInvited.length} broker${alreadyInvited.length === 1 ? " was" : "s were"} already invited. Can't find the email? Resend it.`,
        {
          duration: 12000,
          action: {
            label: "Resend email",
            onClick: () => handleResend(alreadyInvited),
          },
        }
      );
    }
    if (failed.length) {
      toast.error(
        `Failed: ${failed.slice(0, 3).map((f) => `${f.email} (${f.reason})`).join("; ")}${failed.length > 3 ? "…" : ""}`,
        {
          duration: 12000,
          action: {
            label: "Retry these",
            onClick: () => handleResend(failed.map((f) => f.email)),
          },
        }
      );
    }

    if (rateLimitedMsg) {
      toast.error(rateLimitedMsg, { duration: 8000 });
    }

    if (sent.length > 0) {
      setRaw("");
      onSent?.();
    }
    setSending(false);
  };

  const count = parseEmails().length;

  return (
    <Card className="p-5 h-full flex flex-col justify-between bg-brand-teal/[0.03] border-brand-teal/30">
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2 mb-2">
          <UserPlus className="h-4 w-4 text-brand-teal" /> Invite Your Brokers
        </h2>
        <p className="text-xs text-muted-foreground mb-3">
          Enter up to {MAX_EMAILS} broker emails, separated by commas.
        </p>
        <Textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="broker1@example.com, broker2@example.com"
          rows={3}
          className="text-sm resize-none"
          disabled={sending}
        />
        <div className="mt-1.5 text-[11px] text-muted-foreground">
          {count}/{MAX_EMAILS} email{count === 1 ? "" : "s"}
        </div>
        <p className="mt-1 text-[11px] text-brand-teal font-medium">
          Max 10 invites per send.
        </p>
      </div>
      <div className="mt-3 pt-3 border-t border-border space-y-2">
        <Button
          onClick={handleSend}
          disabled={sending || count === 0 || count > MAX_EMAILS}
          className="w-full bg-brand-teal hover:bg-brand-teal/90 text-white"
          size="sm"
        >
          {sending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {progress ? `Sending ${progress.done}/${progress.total}…` : "Sending…"}
            </>
          ) : (
            <><Send className="h-4 w-4 mr-2" /> Send Invites</>
          )}
        </Button>
        <p className="text-[11px] text-muted-foreground leading-snug">
          Invites are sent one at a time so none get dropped. Invitations expire in 30 days. The broker must accept before they can bid on your private loads.
        </p>

      </div>

      <AlertDialog open={showMaxWarning} onOpenChange={setShowMaxWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Too many invites</AlertDialogTitle>
            <AlertDialogDescription>
              Max 10 invites per send. Please send 10 at a time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowMaxWarning(false)}>
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
